/* @jsx h */
import { defineComponent, h, ref, onMounted } from "vue";
import { RESTAURANT_FEATURES } from "@lemmonhq/types";
import { settingsAPI } from "@app/data/api/settings";
import { useAuth } from "@app/vue/providers/Auth";

export const RestaurantFeaturesSection = defineComponent({
  name: "RestaurantFeaturesSection",
  setup() {
    const selectedFeatures = ref<string[]>([]);
    const loading = ref(true);
    const saving = ref(false);
    const saveSuccess = ref(false);
    const auth = useAuth();

    const loadFeatures = async () => {
      try {
        loading.value = true;
        const response = await settingsAPI.getSettings({
          key: "restaurant_features",
        });
        const setting = response.data.find(
          (s) => s.key === "restaurant_features",
        );
        if (setting?.value) {
          try {
            selectedFeatures.value = JSON.parse(String(setting.value));
          } catch {
            selectedFeatures.value = [];
          }
        }
      } catch (e) {
        console.error("Failed to load restaurant features:", e);
      } finally {
        loading.value = false;
      }
    };

    const toggleFeature = async (slug: string) => {
      const current = [...selectedFeatures.value];
      const idx = current.indexOf(slug);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(slug);
      }
      selectedFeatures.value = current;

      try {
        saving.value = true;
        await settingsAPI.bulkUpdateSettings(
          {
            settings: [
              {
                key: "restaurant_features",
                value: JSON.stringify(current),
                value_type: "json",
              },
            ],
          },
          auth,
        );
        saveSuccess.value = true;
        setTimeout(() => {
          saveSuccess.value = false;
        }, 2000);
      } catch (e) {
        console.error("Failed to save restaurant features:", e);
        // Revert
        if (idx >= 0) {
          selectedFeatures.value.push(slug);
        } else {
          selectedFeatures.value.splice(
            selectedFeatures.value.indexOf(slug),
            1,
          );
        }
      } finally {
        saving.value = false;
      }
    };

    onMounted(loadFeatures);

    const groups = [
      { key: "cuisine", label: "Cuisine" },
      { key: "dietary", label: "Dietary" },
      { key: "amenities", label: "Amenities" },
      { key: "experience", label: "Experience" },
    ];

    return () => (
      <div class="card bg-base-200/50 l-box-shadow">
        <div class="card-body p-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-3">
              <span class="material-symbols-outlined text-primary text-xl">
                tune
              </span>
              <div>
                <h3 class="text-lg font-semibold text-accent">
                  Restaurant Features
                </h3>
                <p class="text-sm text-base-content/50">
                  Select features that describe your restaurant. These appear as
                  filters for customers browsing restaurants.
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              {saving.value && (
                <span class="loading loading-spinner loading-xs text-primary"></span>
              )}
              {saveSuccess.value && (
                <span class="text-success text-xs font-medium flex items-center gap-1">
                  <span class="material-symbols-outlined text-sm">
                    check_circle
                  </span>
                  Saved
                </span>
              )}
            </div>
          </div>

          {loading.value ? (
            <div class="flex items-center justify-center py-8">
              <span class="loading loading-spinner loading-sm text-primary"></span>
            </div>
          ) : (
            <div class="space-y-5">
              {groups.map((group) => {
                const features = RESTAURANT_FEATURES.filter(
                  (f) => f.group === group.key,
                );
                return (
                  <div key={group.key}>
                    <h4 class="text-xs font-semibold text-base-content/40 uppercase tracking-wide mb-2">
                      {group.label}
                    </h4>
                    <div class="flex flex-wrap gap-2">
                      {features.map((feature) => {
                        const isSelected = selectedFeatures.value.includes(
                          feature.slug,
                        );
                        return (
                          <button
                            key={feature.slug}
                            type="button"
                            class={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all border cursor-pointer ${
                              isSelected
                                ? "border-primary bg-primary/15 text-primary shadow-sm"
                                : " text-base-content/50 hover:text-base-content/80 hover:border-base-content/30"
                            }`}
                            onClick={() => toggleFeature(feature.slug)}
                            disabled={saving.value}
                          >
                            <span class="material-symbols-outlined text-sm">
                              {feature.icon}
                            </span>
                            {feature.name}
                            {isSelected && (
                              <span class="material-symbols-outlined text-xs">
                                check
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  },
});
